import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Input } from "@/components/ui/input";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const PRODUCTS_ROUTE = "/app/catalog/products";

export function ProductsFilter() {
  const nav = useNavigate({ from: PRODUCTS_ROUTE });
  const search = useSearch({ from: PRODUCTS_ROUTE }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const searchFilter = findCondition(filters, "name");
  const descFilter = findCondition(filters, "description");
  const typeFilter = findCondition(filters, "type");
  const isActiveFilter = findCondition(filters, "isActive");

  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) || (descFilter?.value as string) || "",
  );

  useEffect(() => {
    const nameVal = searchFilter?.value as string;
    const descVal = descFilter?.value as string;
    setSearchValue(nameVal || descVal || "");
  }, [searchFilter?.value, descFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "name", op: "contains", value: val },
        { field: "description", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "name");
      nextFilters = removeConditions(nextFilters ?? [], "description");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const typeValue = (typeFilter?.value as string) ?? "";
  const isActiveValue =
    typeof isActiveFilter?.value === "boolean"
      ? String(isActiveFilter.value)
      : "";
  const hasFilter =
    !!searchFilter?.value ||
    !!descFilter?.value ||
    typeValue !== "" ||
    isActiveValue !== "";

  const clearFilters = () => {
    setSearchValue("");
    nav({
      search: {
        ...search,
        offset: 0,
        filters: undefined,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Input
        placeholder="ຄົ້ນຫາຕາມຊື່ ຫຼື ຄຳອະທິບາຍ..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="max-w-xs"
      />
      <SimpleSelect
        placeholder="ປະເພດ"
        value={typeValue || "all"}
        onValueChange={(v) => {
          const next =
            v === "all"
              ? removeConditions(filters, "type")
              : upsertCondition(filters, { field: "type", op: "eq", value: v });
          nav({
            search: {
              ...search,
              offset: 0,
              filters: next?.length ? next : undefined,
            },
          });
        }}
        options={[
          { value: "all", label: "ທຸກປະເພດ" },
          { value: "MENU", label: "MENU" },
          { value: "RETAIL", label: "RETAIL" },
        ]}
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        value={isActiveValue || "all"}
        onValueChange={(v) => {
          const next =
            v === "all"
              ? removeConditions(filters, "isActive")
              : upsertCondition(filters, {
                  field: "isActive",
                  op: "eq",
                  value: v === "true",
                });
          nav({
            search: {
              ...search,
              offset: 0,
              filters: next?.length ? next : undefined,
            },
          });
        }}
        options={[
          { value: "all", label: "ທຸກສະຖານະ" },
          { value: "true", label: "ເປີດ" },
          { value: "false", label: "ປິດ" },
        ]}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="h-4 w-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
